import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { UserService } from './user.service';
import { NEVER, from, Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { PopupService } from './popup.service';
import { environment } from 'src/environments/environment';

/** API routes that don't require an access token. */
const NO_AUTH_ROUTES = [
  '/register',
  '/login',
  '/sso/',
  '/refresh-token',
  '/validate-email',
  '/forgot-password',
  '/reset-password',
  '/terms-and-conditions',
  '/invite-email',
];

/**
 * Intercepts HTTP requests to provide the access token.
 */
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  /** Guards against the sign-out cascade: many concurrent auth requests can hit the not-signed-in path. */
  private signingOut = false;

  constructor(
    private userService: UserService,
    private popupService: PopupService,
  ) {}

  /**
   * Intercepts HTTP requests and provides the access token in the header.
   * @param request The intercepted HTTP request.
   * @param next The HTTP request to hand to the next interceptor.
   * @returns An HTTP event.
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Skip auth for hierarchy widget local backend (temporary for local dev)
    const widgetApiUrl = (environment as Record<string, unknown>)
      .hierarchyWidgetApiUrl as string;
    if (widgetApiUrl && request.url.startsWith(widgetApiUrl)) {
      return next.handle(request);
    }

    // Temporary: skip Authorization header for library service requests.
    // Library service uses X-Tenant-Id for auth (not Bearer), and its CORS
    // config returns Access-Control-Allow-Origin: * which is incompatible with
    // credentialed requests triggered by the Authorization header.
    // Remove once library service adds proper CORS origin + credentials support.
    // const libraryUrl = (environment as Record<string, unknown>)
    //   .microserviceLibraryUrl as string;
    // if (libraryUrl && request.url.startsWith(libraryUrl)) {
    //   return next.handle(request);
    // }

    // If an auth-required route
    if (NO_AUTH_ROUTES.every((route) => !request.url.includes(route))) {
      // Check if the user is signed in
      return this.checkSignIn().pipe(
        mergeMap((signedIn) => {
          if (!signedIn) {
            // Sign out + redirect to login once (concurrent auth requests would otherwise cascade).
            if (!this.signingOut) {
              this.signingOut = true;
              this.userService.signOut();
              this.popupService.notify(
                'Session Expired. Your session has expired. Please sign in again to complete that action.',
                true,
              );
            }
            // Return NEVER (not EMPTY): EMPTY completes the stream, so downstream first()/firstValueFrom
            // throw EmptyError ("no elements in sequence"), which surfaces as a spurious error toast. NEVER
            // leaves the cancelled request pending; the sign-out redirect tears the page down.
            return NEVER;
          } else {
            const newRequest = this.addToken(request.clone());
            return this.passRequest(newRequest, next);
          }
        }),
      );
    }

    return this.passRequest(request, next);
  }

  /**
   * Adds the access token to the provided request.
   * @param request The request to add the access token.
   * @returns A new request with the access token.
   */
  private addToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const accessToken = this.userService.accessToken;
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken?.token}`,
      },
    });
  }

  /**
   * Checks that the user is signed in.
   * @returns Whether the user is signed in (returned as observable).
   */
  private checkSignIn(): Observable<boolean> {
    return from(this.userService.isSignedIn());
  }

  /**
   * Passes the request on to the next potential interceptor.
   * @param request The new request to be passed on or processed.
   * @param next The HTTP request to hand to the next interceptor.
   * @returns An HTTP event.
   */
  private passRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        // If unauthorized, sign the user out
        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.Unauthorized
        ) {
          this.userService.signOut();
        }
        return throwError(() => error);
      }),
    );
  }
}
